/* p78 · cd-compare: koleksiyon kartlarında "Karşılaştır" seçimi, alttaki çubuk ve tablo diyaloğu.
   Veri her kartın onay kutusundaki data-cd-compare JSON'undan gelir (Liquid; metafield yoksa boş → "—").
   Seçim sessionStorage'da tutulur; Horizon filtre/sonsuz kaydırma ile DOM değişince yeniden eşitlenir. */

const STORE_KEY = 'p78-compare';
const DASH = '—';

/** @typedef {{id:number,title:string,name?:string,vendor:string,url:string,image:string,price:string,unit:string,unitCents:number,servings:number,protein:string,size:string,flavors:number,available:boolean}} Item */

/** @returns {Item[]} */
function load() {
  try {
    const raw = sessionStorage.getItem(STORE_KEY);
    const list = raw ? JSON.parse(raw) : [];
    return Array.isArray(list) ? list : [];
  } catch {
    return [];
  }
}

/** @param {Item[]} list */
function save(list) {
  try {
    sessionStorage.setItem(STORE_KEY, JSON.stringify(list));
  } catch {
    /* gizli pencere: yalnız sayfa içi durum */
  }
}

/** @param {string} text @param {Record<string, string|number>} values */
function fill(text, values) {
  return Object.entries(values).reduce((out, [key, value]) => out.replaceAll(`[${key}]`, String(value)), text || '');
}

/** @param {string} tag @param {string} [className] @param {string} [text] */
function el(tag, className, text) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

function glyph(name) {
  const paths = {
    x: '<path d="M18 6 6 18"/><path d="m6 6 12 12"/>',
  };
  const span = document.createElement('span');
  span.innerHTML = `<svg class="cd-glyph" viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">${paths[name] || ''}</svg>`;
  return /** @type {SVGElement} */ (span.firstElementChild);
}

class CompareController {
  /** @type {Item[]} */
  items = load();
  /** @type {number} */
  #frame = 0;
  /** Karşılaştırma modu (araç çubuğu anahtarı): açıkken kartlarda onay kutuları görünür */
  mode = false;

  /** @param {HTMLElement} root */
  constructor(root) {
    this.bind(root);
  }

  /** @param {HTMLElement} root */
  bind(root) {
    this.root = root;
    this.max = Number(root.dataset.max) || 3;
    try {
      this.labels = JSON.parse(root.dataset.labels || '{}');
    } catch {
      this.labels = {};
    }
    this.slots = root.querySelector('[data-cd-cmp-slots]');
    this.count = root.querySelector('[data-cd-cmp-count]');
    this.status = root.querySelector('[data-cd-cmp-status]');
    this.openButton = root.querySelector('[data-cd-cmp-open]');
    this.dialog = /** @type {HTMLDialogElement|null} */ (document.querySelector('[data-cd-cmp-dialog]'));
    this.table = this.dialog?.querySelector('[data-cd-cmp-table]');
  }

  start() {
    document.documentElement.classList.add('cd-cmp-ready');
    document.addEventListener('change', this.onChange);
    document.addEventListener('click', this.onClick);
    /* Horizon filtre/sonsuz kaydırma DOM'u değiştirir: kökü ve kart kutularını kare başına bir kez eşitle */
    new MutationObserver(() => {
      if (this.#frame) return;
      /* setTimeout: arka plandaki sekmede de çalışır (rAF orada durur, eşitleme sekme öne gelene kadar bekliyordu) */
      this.#frame = window.setTimeout(() => {
        this.#frame = 0;
        const root = document.querySelector('[data-cd-compare-root]');
        if (root instanceof HTMLElement && root !== this.root) {
          this.bind(root);
          this.render();
        } else {
          this.syncChecks();
          this.placeToggles();
        }
      }, 0);
    }).observe(document.body, { childList: true, subtree: true });
    this.render();
  }

  /** @param {Event} event */
  onChange = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.matches('[data-cd-compare-item]')) return;
    let data;
    try {
      data = JSON.parse(input.dataset.cdCompare || '{}');
    } catch {
      return;
    }
    if (input.checked) {
      if (this.items.length >= this.max) {
        input.checked = false;
        this.announce(fill(this.labels.limit, { en_fazla: this.max }));
        return;
      }
      if (!this.items.some((item) => item.id === data.id)) this.items.push(data);
    } else {
      this.items = this.items.filter((item) => item.id !== data.id);
    }
    save(this.items);
    this.render();
  };

  /** @param {MouseEvent} event */
  onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    const remove = target.closest('[data-cd-cmp-remove]');
    if (remove) {
      const id = Number(remove.getAttribute('data-cd-cmp-remove'));
      this.items = this.items.filter((item) => item.id !== id);
      save(this.items);
      this.render();
      this.openButton?.focus();
      return;
    }
    if (target.closest('[data-cd-cmp-mode]')) {
      this.setMode(!this.mode);
      return;
    }
    if (target.closest('[data-cd-cmp-clear]')) {
      this.items = [];
      save(this.items);
      this.render();
      return;
    }
    if (target.closest('[data-cd-cmp-open]')) {
      this.openDialog();
      return;
    }
    if (target.closest('[data-cd-cmp-close]')) {
      this.dialog?.close();
      return;
    }
    if (this.dialog && target === this.dialog) this.dialog.close(); // arka plana tıklama
  };

  /** Anahtarı her araç çubuğuna (masaüstü + mobil facets-controls-wrapper) bir kez yerleştirir; Horizon filtre
      sonrası araç çubuğunu yeniden çizdiğinde MutationObserver bunu tekrar çağırır. */
  placeToggles() {
    const template = document.querySelector('template[data-cd-cmp-toggle]');
    if (!(template instanceof HTMLTemplateElement)) return;
    for (const bar of document.querySelectorAll('.facets-controls-wrapper')) {
      if (bar.querySelector('[data-cd-cmp-mode]')) continue;
      const button = template.content.firstElementChild?.cloneNode(true);
      if (!(button instanceof HTMLElement)) continue;
      const count = bar.querySelector('.products-count-wrapper');
      if (count) count.after(button);
      else bar.prepend(button);
    }
    this.syncToggles();
  }

  syncToggles() {
    const on = this.mode || this.items.length > 0;
    for (const button of document.querySelectorAll('[data-cd-cmp-mode]')) {
      const value = on ? 'true' : 'false';
      if (button.getAttribute('aria-pressed') !== value) button.setAttribute('aria-pressed', value);
    }
  }

  /** @param {boolean} on */
  setMode(on) {
    this.mode = on;
    document.documentElement.classList.toggle('cd-cmp-mode', on);
    this.syncToggles();
  }

  announce(text) {
    if (this.status) this.status.textContent = text || '';
  }

  syncChecks() {
    const ids = new Set(this.items.map((item) => item.id));
    const full = this.items.length >= this.max;
    for (const input of document.querySelectorAll('[data-cd-compare-item]')) {
      if (!(input instanceof HTMLInputElement)) continue;
      const checked = ids.has(Number(input.value));
      if (input.checked !== checked) input.checked = checked;
      input.disabled = full && !checked;
    }
  }

  render() {
    const n = this.items.length;
    document.documentElement.classList.toggle('cd-cmp-active', n > 0);
    this.root.hidden = n === 0;
    if (this.count) this.count.textContent = fill(this.labels.count, { secili: n, en_fazla: this.max });
    if (this.openButton instanceof HTMLButtonElement) this.openButton.disabled = n < 2;

    if (this.slots) {
      this.slots.replaceChildren();
      for (let i = 0; i < this.max; i += 1) {
        const item = this.items[i];
        const li = el('li', `cd-cmp__slot${item ? '' : ' cd-cmp__slot--empty'}`);
        if (item) {
          if (item.image) {
            const img = el('img');
            img.src = item.image;
            img.alt = '';
            img.width = 60;
            img.height = 60;
            img.loading = 'lazy';
            li.append(img);
          }
          li.append(el('span', 'cd-cmp__slot-name', item.name || item.title));
          const remove = el('button', 'cd-cmp__remove');
          remove.type = 'button';
          remove.setAttribute('data-cd-cmp-remove', String(item.id));
          remove.setAttribute('aria-label', `${this.labels.remove || ''}: ${item.title}`);
          remove.append(glyph('x'));
          li.append(remove);
        } else {
          li.append(el('span', 'cd-cmp__slot-name', this.labels.empty || ''));
        }
        this.slots.append(li);
      }
    }
    this.syncChecks();
    this.placeToggles();
  }

  openDialog() {
    if (!this.dialog || !this.table || this.items.length < 2) return;
    const L = this.labels;
    const items = this.items;
    const unitValues = items.map((item) => item.unitCents).filter((value) => value > 0);
    const best = unitValues.length > 1 ? Math.min(...unitValues) : 0;

    const table = el('table', 'cd-cmp-table');
    const thead = el('thead');
    const headRow = el('tr');
    const corner = el('th', '', L.product || '');
    corner.scope = 'col';
    headRow.append(corner);
    for (const item of items) {
      const th = el('th');
      th.scope = 'col';
      const link = /** @type {HTMLAnchorElement} */ (el('a', 'cd-cmp-table__product'));
      link.href = item.url;
      if (item.image) {
        const img = el('img');
        img.src = item.image;
        img.alt = '';
        img.width = 88;
        img.height = 88;
        link.append(img);
      }
      if (item.vendor) {
        const vendor = el('span', 'cd-cmp-table__vendor', item.vendor);
        vendor.lang = 'en';
        link.append(vendor);
      }
      link.append(el('span', 'cd-cmp-table__name', item.name || item.title));
      th.append(link);
      headRow.append(th);
    }
    thead.append(headRow);

    const rows = [
      [L.price, (item) => item.price || DASH, 'cd-cmp-table__price'],
      [
        L.unit,
        (item) => {
          if (!item.unit) return DASH;
          const cell = document.createDocumentFragment();
          cell.append(item.unit);
          if (best && item.unitCents === best) cell.append(el('span', 'cd-cmp-table__best', L.best || ''));
          return cell;
        },
      ],
      [L.servings, (item) => (item.servings > 0 ? String(item.servings) : DASH)],
      [L.protein, (item) => item.protein || DASH],
      [L.size, (item) => item.size || DASH],
      [L.flavors, (item) => (item.flavors > 0 ? String(item.flavors) : DASH)],
      [L.stock, (item) => (item.available ? L.inStock : L.outOfStock) || DASH],
    ];

    const tbody = el('tbody');
    for (const [label, value, className] of rows) {
      const tr = el('tr');
      const th = el('th', '', label || '');
      th.scope = 'row';
      tr.append(th);
      for (const item of items) {
        const td = el('td', className || '');
        const content = value(item);
        td.append(content);
        tr.append(td);
      }
      tbody.append(tr);
    }
    table.append(thead, tbody);
    this.table.replaceChildren(table);
    this.dialog.showModal();
  }
}

const root = document.querySelector('[data-cd-compare-root]');
if (root instanceof HTMLElement && !window.__cdCompare) {
  window.__cdCompare = new CompareController(root);
  window.__cdCompare.start();
}
